import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, Empty, EntityChip, SortHeader, StatusPill, statusOf } from '../components/ui';
import { ago } from '../lib/format';
import { api } from '../lib/api';
import { useApi } from '../lib/useApi';
import { useSort } from '../lib/useSort';

interface ContainerRow {
  id: string;
  name: string;
  image: string;
  imageTag: string | null;
  version: string | null;
  composeService: string | null;
  state: string;
  health: string;
  restartCount: number;
  lastSeenAt: string;
  server: { id: string; name: string; hostname: string };
  client: { id: string; code: string; name: string } | null;
  deployment: {
    id: string;
    name: string;
    version: string | null;
    composePath: string | null;
    product: { key: string; name: string; isInfrastructure: boolean };
  } | null;
}

/**
 * One compose project on one server — the thing that is actually deployed.
 *
 * Keyed by deployment rather than by project name: the same project name exists
 * on four hosts (`knowledgeiq` runs on all of them) and twice on one of them, so
 * a name is not an identity. `null` is the bucket for containers the collector
 * could not attribute to any stack.
 */
interface Stack {
  key: string;
  deployment: ContainerRow['deployment'];
  server: ContainerRow['server'];
  client: ContainerRow['client'];
  containers: ContainerRow[];
}

function groupIntoStacks(rows: ContainerRow[]): Stack[] {
  const byKey = new Map<string, Stack>();
  for (const c of rows) {
    // Unattributed containers still group by server, so they read as "this host
    // has three things we cannot place" rather than as one estate-wide pile.
    const key = c.deployment ? `d:${c.deployment.id}` : `s:${c.server.id}:unattributed`;
    let stack = byKey.get(key);
    if (!stack) {
      stack = { key, deployment: c.deployment, server: c.server, client: c.client, containers: [] };
      byKey.set(key, stack);
    }
    stack.containers.push(c);
  }

  for (const s of byKey.values()) s.containers.sort((a, b) => a.name.localeCompare(b.name));

  return [...byKey.values()].sort(
    (a, b) =>
      a.server.name.localeCompare(b.server.name) ||
      (a.deployment?.product.name ?? 'zzz').localeCompare(b.deployment?.product.name ?? 'zzz'),
  );
}

/** The worst state in a stack, since that is what someone scanning the page wants. */
function worstOf(containers: ContainerRow[]): string {
  if (containers.some((c) => statusOf(c.state) === 'down')) return 'EXITED';
  if (containers.some((c) => statusOf(c.state) === 'warn')) return 'DEGRADED';
  return 'RUNNING';
}

/**
 * Restart or recreate a whole compose stack.
 *
 * Two actions and not one, because the difference is what people get wrong:
 * `restart` bounces the containers and keeps their current environment, while
 * `up -d` recreates them and is the only one that applies an `.env` edit —
 * compose reads `.env` when a container is CREATED, so a restart after an edit
 * looks like the edit silently failed.
 *
 * Both run `docker compose` on the host through platformiq-envctl, so this is
 * genuinely the same operation as deploying by hand, not an approximation of it.
 */
function StackActions({ stack, onDone }: { stack: Stack; onDone: () => void }) {
  const [pending, setPending] = useState<'restart' | 'up' | null>(null);
  const [busy, setBusy] = useState<'restart' | 'up' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const d = stack.deployment!;
  const label = `${d.product.name}${stack.client ? ` (${stack.client.code})` : ''} on ${stack.server.name}`;

  const run = async (action: 'restart' | 'up') => {
    setBusy(action);
    setPending(null);
    setError(null);
    setResult(null);
    try {
      const res = await api<{ action: string; stack: string; server: string; output: string }>(
        `/deployments/${d.id}/compose`,
        { method: 'POST', body: JSON.stringify({ action }) },
      );
      setResult(
        `docker compose ${res.action} finished on ${res.server}.` +
          (res.output ? `\n\n${res.output}` : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The command failed');
    } finally {
      setBusy(null);
      onDone();
    }
  };

  if (pending) {
    return (
      <div className="notice notice-bad stack-confirm">
        <p>
          {pending === 'up' ? 'Recreate' : 'Restart'} all {stack.containers.length} containers of{' '}
          <strong>{label}</strong>? This takes the application offline for a few seconds.
        </p>
        <div className="cost-actions">
          <button type="button" onClick={() => void run(pending)}>
            Yes, {pending === 'up' ? 'recreate' : 'restart'} it
          </button>
          <button type="button" className="ghost" onClick={() => setPending(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="stack-actions">
        <button type="button" className="ghost small" disabled={busy !== null}
          onClick={() => setPending('restart')}>
          {busy === 'restart' ? 'Restarting…' : 'Restart deployment'}
        </button>
        <button type="button" className="ghost small" disabled={busy !== null}
          onClick={() => setPending('up')}>
          {busy === 'up' ? 'Recreating…' : 'Recreate (apply .env)'}
        </button>
      </div>
      {busy && <p className="muted small">This can take a minute if an image needs pulling.</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
      {result && <pre className="compose-output">{result}</pre>}
    </>
  );
}

/** Restart a single container, for when one process is wedged and the rest are fine. */
function ContainerRestart({ c }: { c: ContainerRow }) {
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setPending(false);
    setError(null);
    try {
      const res = await api<{ container: string; server: string }>(`/containers/${c.id}/restart`, {
        method: 'POST',
      });
      setDone(`Restarted ${res.container}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The restart failed');
    } finally {
      setBusy(false);
    }
  };

  if (done) return <span className="muted small">{done}</span>;
  if (error) return <span className="form-error small">{error}</span>;
  if (pending) {
    return (
      <span className="inline-confirm">
        <button type="button" className="small" onClick={() => void run()}>Confirm</button>
        <button type="button" className="ghost small" onClick={() => setPending(false)}>No</button>
      </span>
    );
  }
  return (
    <button type="button" className="ghost small" disabled={busy} onClick={() => setPending(true)}>
      {busy ? 'Restarting…' : 'Restart'}
    </button>
  );
}

/** The container table used inside a stack card and, ungrouped, on its own. */
function ContainerTable({
  rows,
  canRestart,
  showStack,
  sort,
}: {
  rows: ContainerRow[];
  canRestart: boolean;
  showStack: boolean;
  sort?: ReturnType<typeof useSort<ContainerRow>>['state'];
}) {
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            {sort ? <SortHeader label="Container" sortKey="name" state={sort} /> : <th scope="col">Container</th>}
            {showStack &&
              (sort ? <SortHeader label="Deployment" sortKey="stack" state={sort} /> : <th scope="col">Deployment</th>)}
            <th scope="col">Service</th>
            {showStack &&
              (sort ? <SortHeader label="Server" sortKey="server" state={sort} /> : <th scope="col">Server</th>)}
            {sort ? <SortHeader label="Version" sortKey="version" state={sort} /> : <th scope="col">Version</th>}
            {sort ? <SortHeader label="State" sortKey="state" state={sort} /> : <th scope="col">State</th>}
            {sort ? <SortHeader label="Health" sortKey="health" state={sort} /> : <th scope="col">Health</th>}
            {sort ? <SortHeader label="Restarts" sortKey="restarts" state={sort} numeric /> : <th scope="col">Restarts</th>}
            {sort ? <SortHeader label="Seen" sortKey="seen" state={sort} /> : <th scope="col">Seen</th>}
            {canRestart && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td><EntityChip type="container" label={c.name} /></td>
              {showStack && (
                <td>
                  {c.deployment ? (
                    <code className="stack-name" title={c.deployment.composePath ?? undefined}>
                      {c.deployment.name}
                    </code>
                  ) : (
                    <span className="muted">unattributed</span>
                  )}
                </td>
              )}
              {/* The service name inside the compose file — what you would type
                  after `docker compose restart`, as opposed to the container name
                  Docker generates by suffixing it. */}
              <td className="mono small">{c.composeService ?? <span className="muted">—</span>}</td>
              {showStack && <td><Link to={`/servers/${c.server.id}`}>{c.server.name}</Link></td>}
              <td>{c.version ?? <span className="muted">{c.imageTag ?? 'untagged'}</span>}</td>
              <td><StatusPill status={c.state} label={c.state.toLowerCase()} /></td>
              <td>
                {c.health === 'NONE'
                  ? <span className="muted small" title="This image declares no healthcheck, which is not the same as healthy">none</span>
                  : <StatusPill status={c.health} label={c.health.toLowerCase()} />}
              </td>
              <td className="num">{c.restartCount}</td>
              <td title={c.lastSeenAt}>{ago(c.lastSeenAt)}</td>
              {canRestart && <td><ContainerRestart c={c} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Containers() {
  const { data, error, loading, reload } = useApi<ContainerRow[]>('/containers');
  const [query, setQuery] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(false);
  // Grouped by default. The flat list answers "where is this container", the
  // grouped one answers "what is deployed here" — and the second is the question
  // the page is usually open for.
  const [grouped, setGrouped] = useState(true);
  // Restarting is superadmin-only. Asked of the server rather than derived from
  // the role here, so there is exactly one definition of who may reach production.
  const [canRestart, setCanRestart] = useState(false);

  useEffect(() => {
    api<{ canTouchProduction: boolean }>('/capabilities')
      .then((caps) => setCanRestart(caps.canTouchProduction === true))
      .catch(() => setCanRestart(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data ?? []).filter((c) => {
      if (onlyProblems && statusOf(c.state) === 'up' && c.restartCount < 5) return false;
      if (!q) return true;
      return [c.name, c.image, c.server.name, c.server.hostname, c.client?.name,
        c.deployment?.product.name, c.deployment?.name, c.composeService]
        .filter(Boolean)
        .some((f) => f!.toLowerCase().includes(q));
    });
  }, [data, query, onlyProblems]);

  // Filter first, then sort. The other order would sort rows that are about to be
  // discarded, and on a filtered view the ordering must be of what is shown.
  const { sorted: rows, state: sort } = useSort<ContainerRow>(
    filtered,
    {
      name: (c) => c.name,
      stack: (c) => c.deployment?.name,
      server: (c) => c.server.name,
      version: (c) => c.version ?? c.imageTag,
      state: (c) => c.state,
      health: (c) => c.health,
      restarts: (c) => c.restartCount,
      seen: (c) => c.lastSeenAt,
    },
    'name',
  );

  const stacks = useMemo(() => groupIntoStacks(filtered), [filtered]);

  if (loading && !data) return <p className="empty">Loading containers…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;

  return (
    <>
      <div className="page-head">
        <h1>Containers</h1>
        <div className="filters">
          <input
            type="search"
            placeholder="Filter by name, image, deployment, server or client"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter containers"
          />
          <label className="check">
            <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} />
            Group by deployment
          </label>
          <label className="check">
            <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
            Only problems
          </label>
        </div>
      </div>

      {grouped ? (
        <>
          {stacks.map((s) => (
            <Card
              key={s.key}
              title={
                s.deployment ? (
                  <span className="stack-title">
                    <code className="stack-name">{s.deployment.name}</code>
                    <EntityChip type="product" label={s.deployment.product.name}
                      meta={s.deployment.version ?? undefined} />
                    {s.client && <EntityChip type="client" label={s.client.code} />}
                    {s.deployment.product.isInfrastructure && <span className="tag">infra</span>}
                  </span>
                ) : (
                  <span className="stack-title">
                    <span className="muted">Unattributed containers</span>
                  </span>
                )
              }
              actions={<StatusPill status={worstOf(s.containers)} />}
            >
              <p className="muted small stack-meta">
                {s.containers.length} container{s.containers.length === 1 ? '' : 's'} on{' '}
                <Link to={`/servers/${s.server.id}`}>{s.server.name}</Link>
                {/* The compose file's path on the host: the single most useful
                    thing to know before going and running this by hand. */}
                {s.deployment?.composePath && (
                  <> · <code>{s.deployment.composePath}</code></>
                )}
              </p>

              {canRestart && s.deployment && <StackActions stack={s} onDone={reload} />}

              <ContainerTable rows={s.containers} canRestart={canRestart} showStack={false} />
            </Card>
          ))}
          {stacks.length === 0 && <Card><Empty>No containers match.</Empty></Card>}
        </>
      ) : (
        <Card>
          <ContainerTable rows={rows} canRestart={canRestart} showStack sort={sort} />
          {rows.length === 0 && <Empty>No containers match.</Empty>}
        </Card>
      )}

      <p className="muted small">
        Showing {filtered.length} of {data?.length ?? 0} running containers
        {grouped && ` across ${stacks.length} deployment${stacks.length === 1 ? "" : "s"}`}.
      </p>
    </>
  );
}
